import Vue from 'vue';
import { TOAST, showToast } from '@mimir/web/eventBus';
import { logErrorAndShowToast } from '@mimir/web/util/uiError';
import { createKeldaConfig, getKeldaConfigs, deleteKeldaConfig, updateKeldaConfig, getKeldaStatuses, getKeldaStatusForConfiguration } from '@mimir/web/api/globalClientKelda';
import { getTenantConfigArchiveLocations } from '@mimir/web/api/globalClientArchiveLocation';
import {
  createIngestStorageConfig, deleteIngestStorageConfig,
  getIngestStorageConfigs, getSystemConfigTranscriptionLanguageSettings,
  getLiveLocations, getTenantConfig, getTenantConfigTranscriptionLanguageSettings, saveTenantConfig,
  updateIngestStorageConfig, updateTenantConfigTranscriptionLanguageSettings, updateTenantConfig } from '@mimir/web/util/api';
import type { TranscriptionService } from '@mimir/common/dto/asyncOperationItemCreationTypes';
import type { TranscriptionOption, CreateTranscriptOptionDto } from '@mimir/common/dto/asyncOperationModels';
import { TranscriptionServices } from '@mimir/common/dto/asyncOperationItemCreationTypes';
import type { IngestStorageConfigClientDto} from '@mimir/common/dto/ingestStorageConfigDto';
import { isNewConfig } from '@mimir/common/dto/ingestStorageConfigDto';
import type { MetadataTypeDefaults, TenantConfigDto, UpdateTenantConfigDto } from '@mimir/common/dto/tenantModel';
import { normalizeNleAsset } from '@mimir/common/util/nleAssetUtils';
import type {
  SupportedLanguage
} from '@mimir/common/util/languages';
import {
  isSupportedSpeechToTextService
} from '@mimir/common/util/languages';
import { invariant } from '@mimir/common/util/invariant';
import cloneDeep from 'lodash/cloneDeep';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import type { Module, Store, Dispatch } from 'vuex';
import type { State } from './state';
import type { CreateKeldaConfigDto, KeldaConfigDto, UpdateKeldaConfigDto } from '@mimir/common/dto/keldaConfigDto';
import { repeatWithCooldownUntilCancelled } from '@mimir/common/async/repeat';
import type { CancelTokenSource } from 'axios';
import rawAxios from 'axios';
import type { KeldaStatusDto } from '@mimir/common/dto/statusDto';
import { clientPollingRateMs } from '@mimir/common/dto/statusDto';
import type LiveLocationDto from '@mimir/common/dto/LiveLocationDto';
import type { ArchiveLocationClientDto } from '@mimir/common/dto/archiveLocationDto';
import type { ConfigModuleState, EditableTranscriptionOption, LanguageToServices } from './config-module-state';
import { assumeEmbedCollection } from '@mimir/common/client/assumeResult';
import { showSimpleDialogFromRequest } from '@mimir/web/dialogs/simple';
import type { SimpleDialogRequest } from '../dialogs/simpleDialogRequest';
import { showSimpleAsyncDialog } from '../dialogs/simpleAsync';
import { defaultTenantConfig } from '@mimir/common/models/tenant-config';
import { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { Filters } from '@mimir/common/filters/Filters';
import { migratePluginToNewFormat } from '@mimir/common/util/plugins';

class ConfigState {
  pollCancel: CancelTokenSource | null = null;

  // If set - we will poll for a specific ID only.
  pollSpecificId: string | null = null;

  async startKeldaStatusPolling(store: Store<State>) {
    if (this.pollCancel !== null)
      return;

    const cts = rawAxios.CancelToken.source();
    this.pollCancel = cts;
    try {
      await repeatWithCooldownUntilCancelled(() => this.doPoll(store), { minimum: clientPollingRateMs, performanceNow: () => performance.now() }, cts.token);
    } finally {
      this.pollCancel = null;
    }
  }

  async stopKeldaStatusPolling() {
    if (this.pollCancel) {
      this.pollCancel.cancel();
    }
  }

  doPoll = async (store: Store<State>) => {
    let statuses: KeldaStatusDto[] = [];
    if (this.pollSpecificId === null) {
      statuses = await getKeldaStatuses();
    } else {
      statuses = await getKeldaStatusForConfiguration(this.pollSpecificId);
    }
    store.commit('setStatuses', { dtos: statuses, specificId: this.pollSpecificId });
  };
}

export const configState = new ConfigState();



export const configModule: Module<ConfigModuleState, State> = {
  state: {
    tenantConfig: defaultTenantConfig(),
    isSaving: false,
    bootstrapped: false,
    transcriptionOptionList: [],
    editableTranscriptionOptionList: [],
    tenantLocationList: [],
    languageList: [],
    languageMap: {},
    optionKeys: 0,
    hasEditedLanguageTab: false,
    isLanguageTabValid: false,
    isTranscriptTabValid: false,
    selectedIngestConfig: null,
    editableSelectedIngestConfig: null,
    ingestConfigs: [],
    ingestConfigIsValid: true,
    keldaConfigs: [],
    selectedKeldaConfig: null,
    editableSelectedKeldaConfig: null,
    keldaStatuses: {},
    archiveLocations: []
  },
  mutations: {
    setStatuses: (state, val: { dtos: KeldaStatusDto[], specificId: string | null }) => {
      const  { dtos, specificId } = val;
      const statuses = groupBy(dtos, status => status.configId);
      // If we're polling for a specific id only, let's keep the other id's intact so nodes don't disappear
      // when the user goes back to the list of configurations temporarily until the next poll kicks in.
      if (specificId !== null) {
        Vue.set(state.keldaStatuses, specificId, statuses[specificId]);
      } else {
        Vue.set(state, 'keldaStatuses', statuses);
      }
    },
    setHasEditedLanguageTab: (state, val: boolean) => state.hasEditedLanguageTab = val,
    setIsLanguageTabValid: (state, val: boolean) => state.isLanguageTabValid = val,
    setIsTranscriptTabValid: (state, val: boolean) => state.isTranscriptTabValid = val,
    setIsSaving: (state, val: boolean) => state.isSaving = val,
    setLanguages: (state, val: SupportedLanguage[]) => state.languageList = val,
    setLanguageMap: (state, val: LanguageToServices) => state.languageMap = val,
    setTenantConfig: (state, val: TenantConfigDto) => {
      const config = cloneDeep(val);
      if (Array.isArray(config.projectTemplates) && config.projectTemplates.length > 0) {
        config.projectTemplates = config.projectTemplates.map(normalizeNleAsset);
      }
      if (Array.isArray(config.presets) && config.presets.length > 0) {
        config.presets = config.presets.map(normalizeNleAsset);
      }
      state.tenantConfig = config;
    },
    normalizeSelectedConfig: state => {
      const copy = cloneDeep(state.editableSelectedIngestConfig);
      if (state.selectedIngestConfig && copy) {
        state.selectedIngestConfig.defaultMetadata = copy.defaultMetadata;
        state.selectedIngestConfig.defaultTimedMetadata = copy.defaultTimedMetadata;
        state.selectedIngestConfig.analyticConfig = copy.analyticConfig;
      } else {
        state.selectedIngestConfig = copy;
      }

    },
    setSelectedIngestConfig: (state, val: IngestStorageConfigClientDto | null) => {
      const copy = cloneDeep(val);
      state.selectedIngestConfig = copy;
      state.editableSelectedIngestConfig = cloneDeep(state.selectedIngestConfig);
    },
    setEditableSelectedIngestConfig: (state, val: IngestStorageConfigClientDto | null) => {
      const copy = cloneDeep(val);
      state.editableSelectedIngestConfig = copy;
    },
    resetSelectedIngestConfig: state => {
      state.editableSelectedIngestConfig = cloneDeep(state.selectedIngestConfig);
    },
    setIngestConfigs: (state, val: IngestStorageConfigClientDto[]) => state.ingestConfigs = val,
    setArchiveLocations: (state, val: ArchiveLocationClientDto[]) => state.archiveLocations = val,
    removeArchiveLocation: (state, location: ArchiveLocationClientDto) => {
      const index = state.archiveLocations.findIndex(item => item.id === location.id);
      if (index === -1) return;

      state.archiveLocations.splice(index, 1);
    },
    addIngestConfig: (state, val: IngestStorageConfigClientDto) => state.ingestConfigs.push(val),
    replaceIngestConfig: (state, val: { newConfig: IngestStorageConfigClientDto, replaceAt: number }) => state.ingestConfigs.splice(val.replaceAt, 1, val.newConfig),
    removeIngestConfig: (state, val: IngestStorageConfigClientDto) => {
      const index = state.ingestConfigs.findIndex(config => val.id === config.id);
      if (index >= 0) {
        state.ingestConfigs.splice(index, 1);
      }
    },
    setIngestConfigIsValid: (state, val: string | true) => state.ingestConfigIsValid = val,
    setTranscriptionOptionList: (state, val: TranscriptionOption[]) => {
      state.transcriptionOptionList = val;
      state.editableTranscriptionOptionList = val.map(item => {
        return {
          ...item,
          id: ++state.optionKeys
        };
      });
      state.bootstrapped = true;
    },
    removeTranscriptionOption: (state, val: EditableTranscriptionOption) => {
      const index = state.editableTranscriptionOptionList.indexOf(val);
      if (index >= 0) {
        state.editableTranscriptionOptionList.splice(index, 1);
      }
    },
    resetTranscriptionTab: state => {
      state.optionKeys = 0;
      state.editableTranscriptionOptionList = state.transcriptionOptionList.map(item => {
        return {
          ...item,
          id: ++state.optionKeys
        };
      });
      state.hasEditedLanguageTab = false;
    },
    addTranscriptionOption: state => {
      state.editableTranscriptionOptionList.unshift({
        label: '',
        id: ++state.optionKeys,
      });
    },
    setTenantLocationList: (state, val: LiveLocationDto[]) => {
      state.tenantLocationList = val;
    },
    addNewLocationOption: (state, createdLocation) => {
      state.tenantLocationList.push(createdLocation);
    },
    updateNewLocationOption: (state, updatedLocation: LiveLocationDto) => {
      const index = state.tenantLocationList.findIndex(location => location.id === updatedLocation.id);
      if (index >= 0) {
        Vue.set(state.tenantLocationList, index, updatedLocation);
      }
    },
    removeLocation: (state, val) => {
      const index = state.tenantLocationList.indexOf(val);
      if (index >= 0) {
        state.tenantLocationList.splice(index, 1);
      }
    },
    setKeldaConfigs: (state, val: KeldaConfigDto[]) => state.keldaConfigs = val,
    addKeldaConfig: (state, val: KeldaConfigDto) => state.keldaConfigs.push(val),
    setSelectedKeldaConfig: (state, val: KeldaConfigDto) => {
      state.selectedKeldaConfig = val;
      state.editableSelectedKeldaConfig = cloneDeep(val);
    },
    replaceKeldaConfig: (state, val: KeldaConfigDto) => {
      const existingIndex = state.keldaConfigs.findIndex(conf => conf.id === val.id);
      if (existingIndex >= 0) {
        state.keldaConfigs.splice(existingIndex, 1, val);
      }

      if (state.selectedKeldaConfig?.id === val.id) {
        state.selectedKeldaConfig = val;
        state.editableSelectedKeldaConfig = cloneDeep(val);
      }
    },
    removeKeldaConfig: (state, val: KeldaConfigDto) => {
      const index = state.keldaConfigs.indexOf(val);
      if (index >= 0) {
        state.keldaConfigs.splice(index, 1);
      }
      if (state.editableSelectedKeldaConfig?.id === val.id) {
        state.selectedKeldaConfig = null;
        state.editableSelectedKeldaConfig = null;
      }
    }
  },
  getters: {
    keldaConfigs: state => state.keldaConfigs,
    selectedKeldaConfig: state => state.editableSelectedKeldaConfig,
    tenantConfig: state => state.tenantConfig,
    configIsSaving: state => state.isSaving,
    supportedLanguages: state => state.languageList,
    transcriptionOptionList: state => state.editableTranscriptionOptionList,
    languageMap: state => state.languageMap,
    hasEditedLanguageTab: state => state.hasEditedLanguageTab,
    tenantLocationList: state => state.tenantLocationList,
    showScheduler: (state, getters, rootState) => {
      // Only show scheduler if user data is loaded and there are visible locations
      const user = rootState.user;
      if (!user || !user.groups || !Array.isArray(user.groups)) {
        return false;
      }

      // Backend already filters locations based on visibility permissions
      // so we just need to check if there are any locations available
      return state.tenantLocationList.length > 0;
    },
    hasEditedIngestConfig: state => {
      if (!state.selectedIngestConfig || !state.editableSelectedIngestConfig) {
        return false;
      }
      // If it's a new config, user has definitely made edits.
      if (isNewConfig(state.selectedIngestConfig)) {
        return true;
      } else {
        return !isEqual(state.editableSelectedIngestConfig, state.selectedIngestConfig);
      }
    },
    isLanguageTabValid: state => state.isLanguageTabValid,
    isTranscriptTabValid: state => state.isTranscriptTabValid,
    selectedIngestConfig: state => state.editableSelectedIngestConfig,
    ingestConfigs: state => state.ingestConfigs,
    ingestConfigIsValid: state => state.ingestConfigIsValid,
    keldaStatuses: state => state.keldaStatuses,
    featureFlags: state => state.tenantConfig.featureFlags,
    shouldDefaultExportMdfOverride: state => state.tenantConfig.shouldDefaultExportMdfOverride ?? true,
    metadataDefaults: state => state.tenantConfig.metadataDefaults,
    showPlayoutFeatures: state => state.tenantConfig.featureFlags && state.tenantConfig.featureFlags['playout'] === true,
    archiveLocations: state => state.archiveLocations,
    usePremierePreferenceForScaleToFrame: state => state.tenantConfig.usePremierePreferenceForScaleToFrame || false,
  },
  actions: {
    loadKeldaConfigs: async context => {
      try {
        const configs = await getKeldaConfigs();
        context.commit('setKeldaConfigs', configs);
        const filters = context.getters.filters as Filters | undefined;
        if (filters) {
          filters.defaultFilters['filter:ingestedByKelda'].setKeldaConfigs(configs);
        }
      } catch (err) {
        logErrorAndShowToast('An error occurred. Please try again later', err, 'ex in loadKeldaConfigs');
      }
    },
    createKeldaConfig: async (context, label: string) => {
      try {
        const newDto: CreateKeldaConfigDto = await createKeldaConfig({
          name: label,
        });
        context.commit('addKeldaConfig', newDto);
        context.commit('setSelectedKeldaConfig', newDto);
        return newDto;
      } catch (err) {
        logErrorAndShowToast('An error occurred. Please try again later', err, 'ex in createKeldaConfig');
        return null;
      }
    },
    deleteKeldaConfig: async (context, config: KeldaConfigDto) => {
      try {
        await deleteKeldaConfig(config.id);
        context.commit('removeKeldaConfig', config);
      } catch (err) {
        logErrorAndShowToast('An error occurred. Please try again later', err, 'ex in deleteKeldaConfig');
      }
    },
    saveKeldaConfig: async (
      context,
      params: { updateDto: UpdateKeldaConfigDto, toastMessage: string }
    ) => {
      try {
        const updatedDto = await updateKeldaConfig(params.updateDto);
        context.commit('replaceKeldaConfig', updatedDto);
        showToast(params.toastMessage, TOAST.SUCCESS);
      } catch (err) {
        logErrorAndShowToast('An error occurred. Please try again later', err, 'ex in saveKeldaConfig');
      }
    },
    loadTranscriptionLanguageTab: async context => {
      try {
        const languages = await getSystemConfigTranscriptionLanguageSettings();
        context.commit('setLanguages', assumeEmbedCollection<SupportedLanguage>(languages));
        const transcriptionOptionList = await getTenantConfigTranscriptionLanguageSettings();
        context.commit('setTranscriptionOptionList', assumeEmbedCollection<TranscriptionOption>(transcriptionOptionList));
      } catch (err) {
        const errorMessage = 'Error while getting transcription language settings';
        console.error(`${errorMessage}: ${err}`);
        logErrorAndShowToast('Error while getting live locations', err);
      }

      const languageMap = context.state.languageList.reduce((map: LanguageToServices, language: SupportedLanguage) => {
        // Construct an array of allowed transcription services for this language
        const transcriptionServices: TranscriptionService[] = [];
        for (const transcriptionService of TranscriptionServices) {
          if (isSupportedSpeechToTextService(language, transcriptionService)) {
            transcriptionServices.push(transcriptionService);
          }
        }

        // Add the minimal fields to the result map, including the array
        map[language.languageCode] = {
          language,
          transcriptionServices
        };
        return map;
      }, {});
      context.commit('setLanguageMap', languageMap);
    },
    saveTranscriptionLanguageTab: async context => {
      try {
        context.commit('setIsSaving', true);
        const newOptions = prepareTranscriptionOptions(context.state.editableTranscriptionOptionList);
        const languageDtos: CreateTranscriptOptionDto[] = [];
        for (const option of newOptions) {
          if (!option.transcriptionService) {
            throw new Error(`Invalid service detected for ${option.label}:${option.languageCode}`);
          } else {
            languageDtos.push({
              label: option.label,
              languageCode: option.languageCode,
              transcriptionService: option.transcriptionService
            });
          }
        }

        await updateTenantConfigTranscriptionLanguageSettings(languageDtos);
        // Update the latest saved set, so discarding a consequent save will not discard already saved changes.
        context.state.transcriptionOptionList = newOptions;
        context.state.hasEditedLanguageTab = false;
        showToast('Transcription language settings saved', TOAST.SUCCESS);
      } catch (err) {
        logErrorAndShowToast('Error while saving transcription language settings', err);
      } finally {
        context.commit('setIsSaving', false);
      }
    },
    loadLiveLocations: async context => {
      try {
        const locationSettings = await getLiveLocations();
        context.commit('setTenantLocationList', locationSettings);
      } catch (err) {
        logErrorAndShowToast('Error while getting tenant location settings', err);
      }
    },
    loadTenantConfig: async (context) => {
      try {
        const config = await getTenantConfig();
        if (config.plugins) {
          // Display-only migration fallback - backend migration in postDeploySteps should handle persistence
          // Always run migration to merge legacy plugins with pluginConfig array
          config.pluginConfig = migratePluginToNewFormat(config, config.plugins);
        }
        context.commit('setTenantConfig', {...context.state.tenantConfig, ...config});
        // Signal root/other modules that tenant configuration has been changed, so other dependent state can update.
        context.commit('tenantConfigUpdated', context.state.tenantConfig);
      } catch (error) {
        showToast('Failed to load configuration, please contact your System Administrator', TOAST.ERROR);
        console.error('Could not load settings', error);
        return;
      }

      // If the metadataDefaults are not set, we copy the mdfTypeDefaults to metadataDefaults.
      await migrateMdfTypeDefaultsToMetadataDefaults(context.dispatch, context.getters.tenantConfig);
    },
    saveTenantConfig: async (context, newConfig: UpdateTenantConfigDto) => {
      context.commit('setIsSaving', true);
      return saveTenantConfig(newConfig).then(savedConfig => {

        context.commit('setTenantConfig', savedConfig);

        // Signal root/other modules that tenant configuration has been changed, so other dependent state can update.
        context.commit('tenantConfigUpdated', context.state.tenantConfig);
      }).finally(() => context.commit('setIsSaving', false));
    },
    updateTenantConfig: async (context, newConfig: UpdateTenantConfigDto) => {
      context.commit('setIsSaving', true);
      // update returns the whole tenantConfigDto
      return updateTenantConfig(newConfig).then(savedConfig => {
        context.commit('setTenantConfig', savedConfig);
        // Signal root/other modules that tenant configuration has been changed, so other dependent state can update.
        context.commit('tenantConfigUpdated', context.state.tenantConfig);
      }).finally(() => context.commit('setIsSaving', false));
    },
    loadIngestConfigs: async context => {
      try {
        const ingestStorageConfigs = await getIngestStorageConfigs();
        context.commit('setIngestConfigs', ingestStorageConfigs);
        if (ingestStorageConfigs.length > 0) {
          context.commit('setSelectedIngestConfig', ingestStorageConfigs[0]);
        }
      } catch (err) {
        logErrorAndShowToast('An error occurred while trying to load ingest configurations', err);
      }
    },
    loadArchiveLocations: async context => {
      try {
        const archiveLocations = await getTenantConfigArchiveLocations();
        context.commit('setArchiveLocations', archiveLocations);
      } catch (err) {
        logErrorAndShowToast('An error occurred while trying to load archive locations', err);
      }
    },
    saveSelectedIngestConfig: async context => {
      const { editableSelectedIngestConfig, selectedIngestConfig } = context.state;
      invariant(editableSelectedIngestConfig, 'Expected selected editable ingest config');
      invariant(selectedIngestConfig, 'Expected selected config');

      context.commit('setIsSaving', true);
      if (!isNewConfig(editableSelectedIngestConfig)) {
        return updateIngestStorageConfig(editableSelectedIngestConfig.id, editableSelectedIngestConfig).then(newConfig => {
          context.commit('removeIngestConfig', selectedIngestConfig);
          context.commit('addIngestConfig', newConfig);
          context.commit('setSelectedIngestConfig', newConfig);
          showToast('Ingest config updated', TOAST.SUCCESS);
        }).catch(() => {
          void context.dispatch('handleIngestConfigSaveFailure').catch(noop);
        }).finally(() => context.commit('setIsSaving', false));
      } else {
        editableSelectedIngestConfig.id = ''; // I don't like doing this, but I'm trying to avoid lambda changes for now. Lambda expects empty string.
        return createIngestStorageConfig(editableSelectedIngestConfig).then(newConfig => {
          context.commit('removeIngestConfig', selectedIngestConfig);
          context.commit('addIngestConfig', newConfig);
          context.commit('setSelectedIngestConfig', newConfig);
          showToast('New config saved', TOAST.SUCCESS);
        }).catch(() => {
          void context.dispatch('handleIngestConfigSaveFailure').catch(noop);
        }).finally(() => context.commit('setIsSaving', false));
      }
    },
    handleIngestConfigSaveFailure: async (context) => {
      const { selectedIngestConfig } = context.state;
      invariant(selectedIngestConfig, 'Expected ingest config');
      const header = 'Error saving ingest config';
      const safeHtmlBuilder = new SafeHtmlBuilder()
        .addText('An error occurred while trying to save the ingest configuration.')
        .addLineBreakAndAppendText('Click \'Retry\' to try again.');

      const dialogRequest: SimpleDialogRequest = {
        dialogProps: {
          maxWidth: 450,
          icon: 'error',
          actionText: 'Retry',
          cancelText: 'Cancel',
          header,
          baseHtml: safeHtmlBuilder,
          closeOnActionClick: true
        },
        callback: (res) => {
          if (res === 'ok') {
            void context.dispatch('saveCurrentIngestConfig').catch(noop);
          }
        }
      };

      showSimpleDialogFromRequest(dialogRequest);
    },
    deleteSelectedIngestConfig: async ({ state, commit }) => {
      const { selectedIngestConfig } = state;
      invariant(selectedIngestConfig, 'Expected selected ingest config to delete');
      void showSimpleAsyncDialog({
        maxWidth: 450,
        icon: 'delete',
        header: 'Delete ingest storage config',
        baseText: `Are you sure you want to delete the ingest storage config '${selectedIngestConfig.title}' ?`,
        actionText: 'Remove',
        closeOnActionClick: true,
      },
      async () => {
        // Existing dto - needs to delete serverside
        if (!isNewConfig(selectedIngestConfig)) {
          return deleteIngestStorageConfig(selectedIngestConfig.id).then(() => {
            commit('removeIngestConfig', selectedIngestConfig);
            commit('setSelectedIngestConfig', state.ingestConfigs.length > 0 ? state.ingestConfigs[0] : null);
            showToast('Ingest config deleted', TOAST.INFO);
          }).catch(reason => {
            showToast('Failed to delete config', TOAST.ERROR);
            console.error(`Failed to delete ingest config ${JSON.stringify(reason, null, 2)}`);
          });
        }
        // New DTO not saved yet, just need to remove client side
        commit('removeIngestConfig', selectedIngestConfig);
        commit('setSelectedIngestConfig', state.ingestConfigs.length > 0 ? state.ingestConfigs[0] : null);
      });
    },
    resetSelectedIngestConfig: ({ state, commit }) => {
      invariant(state.editableSelectedIngestConfig, 'Expected selected editable ingest config');
      invariant(state.selectedIngestConfig, 'Expected selected config');

      // A new ingest was created that was not saved yet. Just need to remove it again.
      if (isNewConfig(state.selectedIngestConfig)) {
        commit('removeIngestConfig', state.selectedIngestConfig);
        commit('setSelectedIngestConfig', state.ingestConfigs.length > 0 ? state.ingestConfigs[0] : null);
      } else {
        // Otherwise just reset the current selected config
        commit('resetSelectedIngestConfig');
      }
    }
  },
};

const prepareTranscriptionOptions = (items: EditableTranscriptionOption[]): TranscriptionOption[] => {
  const transcriptionOptions: TranscriptionOption[] = [];
  for (const item of items) {
    if (item.languageCode && item.transcriptionService) {
      transcriptionOptions.push({
        label: item.label,
        languageCode: item.languageCode,
        transcriptionService: item.transcriptionService
      });
    }
  }
  return transcriptionOptions;
};


const migrateMdfTypeDefaultsToMetadataDefaults =  async (dispatch: Dispatch, tenantConfig: TenantConfigDto) => {
  if (tenantConfig.mdfTypeDefaults && !tenantConfig.metadataDefaults) {
    const metadataDefaults: Partial<Record<MetadataTypeDefaults, ItemMetaData>> = {};
    for (const key of Object.keys(tenantConfig.mdfTypeDefaults) as MetadataTypeDefaults[]) {
      const formId = tenantConfig.mdfTypeDefaults[key];
      if (!formId) continue;
      metadataDefaults[key] = { formId, formData: {} };
    }

    try {
      await dispatch('updateTenantConfig', {
        ...tenantConfig,
        metadataDefaults
      });
    } catch (error) {
      console.error('Could not updateTenantConfig with metadataDefaults ', error);
    }
  }
};
